import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { MessageSquare, Bot, Search, Send, Settings, QrCode, Loader2, Wifi, WifiOff, AlertTriangle, FlaskConical, ArrowLeft } from "lucide-react";

import useChatBot from "@/hooks/useChatBot";
import ContactItem from "@/components/chatbot/ContactItem";
import MessageBubble from "@/components/chatbot/MessageBubble";
import QRConnectionPanel from "@/components/chatbot/QRConnectionPanel";
import AISessionPanel from "@/components/chatbot/AISessionPanel";
import CustomerDetail from "@/components/chatbot/CustomerDetail";
import { ChatBotSettingsPanel } from "@/components/chatbot/ChatBotSettingsPanel";

export default function ChatBotPage() {
  const { authAxios } = useAuth();
  const {
    filteredContacts, selectedContact, setSelectedContact,
    messages, newMessage, setNewMessage, searchQuery, setSearchQuery,
    chatBotEnabled, connected, setConnected, waStatus, waDisconnectReason, v2Running, connectedPhone,
    botSettings, sending, activeSession, messagesEndRef,
    handleSend, toggleBot, toggleContactBot, saveSettings, handleDisconnect, fetchSettings, fetchWAStatus,
  } = useChatBot(authAxios);

  const [showSettings, setShowSettings] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showCustomerDetail, setShowCustomerDetail] = useState(null);
  const [testingSend, setTestingSend] = useState(false);
  const [testingAI, setTestingAI] = useState(false);
  const [mobileView, setMobileView] = useState("contacts"); // "contacts" | "chat"
  const aiName = botSettings.ai_name || "Asistente";

  async function handleTestAI() {
    setTestingAI(true);
    try {
      const resp = await authAxios.get("/chatbot/test-ai");
      const data = resp.data;
      if (data.ok) {
        toast.success(`✅ IA FUNCIONANDO — chave: ${data.key_preview}`);
      } else {
        toast.error(`❌ IA com ERRO — ${data.error}: ${data.detail || "sem detalhes"}`);
      }
    } catch (err) {
      toast.error(`❌ Erro ao testar IA: ${err.message}`);
    } finally {
      setTestingAI(false);
    }
  }

  async function handleTestSend() {
    if (!connectedPhone) {
      toast.error("WhatsApp não está conectado");
      return;
    }
    const connectedDigits = connectedPhone.replace(/\D/g, "");
    const hint = "Ex: o seu telemóvel pessoal (com DDI, só dígitos). Não use o número do WhatsApp conectado.";
    const recipient = window.prompt(
      `Número para receber o teste.\n${hint}\n\nConectado: +${connectedDigits}`,
      "",
    );
    if (!recipient) return;
    const phone = recipient.replace(/\D/g, "");
    if (!phone || phone.length < 9) {
      toast.error("Número inválido — use DDI + número (ex: 34600123456)");
      return;
    }
    if (phone === connectedDigits || phone.endsWith(connectedDigits.slice(-9))) {
      toast.error(`Use outro número que não seja o WhatsApp conectado (+${connectedDigits})`);
      return;
    }
    setTestingSend(true);
    try {
      const message = "✅ Teste de envio WhatsApp — sistema funcionando corretamente!";
      const resp = await authAxios.post(
        `/chatbot/send-test-notification?phone=${encodeURIComponent(phone)}&message=${encodeURIComponent(message)}`
      );
      const data = resp.data;
      if (data.success) {
        toast.success(`✅ Mensagem enviada para +${phone}. Verifique esse telemóvel no WhatsApp.`);
      } else {
        toast.error(`❌ FALHA no envio — ${data.error || JSON.stringify(data.response) || "erro desconhecido"}. URL do serviço: ${data.whatsapp_service_url || "?"}`);
      }
    } catch (err) {
      const data = err?.response?.data;
      const detailObj = data?.detail;
      const detail = typeof detailObj === "object" && detailObj?.message
        ? detailObj.message
        : typeof data?.error === "string"
          ? data.error
          : detailObj
            ? JSON.stringify(detailObj)
            : err.message || "erro de rede";
      const status = err?.response?.status || "";
      toast.error(`❌ Erro ao testar WhatsApp${status ? ` (${status})` : ""}: ${detail}`);
    } finally {
      setTestingSend(false);
    }
  }

  const handleResetSession = async () => {
    try {
      await authAxios.post("/chatbot/whatsapp/reset-session");
      toast.info("Sessão resetada — aguarde e clique em Reconectar para gerar novo QR.");
      fetchWAStatus();
      setTimeout(() => { fetchWAStatus(); setShowQR(true); }, 1500);
    } catch {
      toast.error("Erro ao resetar sessão");
    }
  };

  // Warn if connected but session is stale (no reconnect in 5+ days)
  const staleConnection = connected && botSettings.last_connected_at &&
    (Date.now() - new Date(botSettings.last_connected_at).getTime()) > 5 * 24 * 60 * 60 * 1000;
  const staleAgeDays = staleConnection
    ? Math.floor((Date.now() - new Date(botSettings.last_connected_at).getTime()) / (24 * 60 * 60 * 1000))
    : 0;

  return (
    <div className="h-screen flex flex-col bg-gray-100" data-testid="chatbot-page">
      {/* Top Bar */}
      <div className="bg-emerald-600 text-white flex flex-wrap items-center justify-between px-3 py-2 gap-y-1 flex-shrink-0">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="font-bold text-sm tracking-wide">WHATSAPP IA - {aiName.toUpperCase()}</h1>
          <button className="flex items-center gap-1.5 text-sm hover:bg-white/15 px-3 py-1.5 rounded" onClick={() => setShowSettings(true)} data-testid="configure-integration-btn">
            <Settings className="w-4 h-4" /> CONFIGURAR
          </button>
          {connected ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-emerald-200 font-mono flex items-center gap-1"><Wifi className="w-3 h-3" /> +{connectedPhone}</span>
              <button
                className="flex items-center gap-1.5 text-sm hover:bg-white/15 px-3 py-1.5 rounded text-yellow-200 disabled:opacity-50"
                onClick={handleTestSend}
                disabled={testingSend}
                data-testid="test-whatsapp-send-btn"
                title="Envia mensagem de teste para outro número (não o WhatsApp conectado)"
              >
                {testingSend ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
                TESTAR ENVIO
              </button>
              <button className="flex items-center gap-1.5 text-sm hover:bg-white/15 px-3 py-1.5 rounded text-red-200" onClick={handleDisconnect} data-testid="disconnect-whatsapp-btn">
                DESCONECTAR
              </button>
            </div>
          ) : (
            <button className="flex items-center gap-1.5 text-sm hover:bg-white/15 px-3 py-1.5 rounded" onClick={() => setShowQR(true)} data-testid="connect-whatsapp-btn-top">
              <QrCode className="w-4 h-4" /> CONECTAR
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            className="flex items-center gap-1.5 text-xs hover:bg-white/15 px-2 py-1 rounded text-blue-200 disabled:opacity-50"
            onClick={handleTestAI}
            disabled={testingAI}
            title="Testa se a chave Anthropic está funcionando"
          >
            {testingAI ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bot className="w-3 h-3" />}
            TESTAR IA
          </button>
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${chatBotEnabled ? "bg-green-300" : "bg-red-300"}`} />
            <span className="text-sm font-bold">IA</span>
            <Switch checked={chatBotEnabled} onCheckedChange={toggleBot} className="scale-90" data-testid="chatbot-toggle" />
          </div>
        </div>
      </div>

      {/* WhatsApp disconnected — red banner */}
      {!connected && waStatus !== "connecting" && (
        <div className="bg-red-600 px-4 py-2 flex items-center justify-between gap-2 text-white text-sm flex-shrink-0" data-testid="wa-disconnected-banner">
          <div className="flex items-center gap-2 min-w-0">
            <WifiOff className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">
              <strong>
                {waDisconnectReason === "v2_runtime_inactive"
                  ? "V2 desconectado — runtime inativo"
                  : "WhatsApp desconectado"}
              </strong>
              {waDisconnectReason === "v2_runtime_inactive" && " — reconecte para activar o socket V2"}
              {waStatus === "reconnect_required" && waDisconnectReason !== "v2_runtime_inactive" && " — reconexão necessária"}
              {waStatus === "qr_required" && " — escaneie o QR Code"}
              {waStatus === "disconnected" && waDisconnectReason !== "v2_runtime_inactive" && " — mensagens não entregues"}
              {waStatus === "stale" && " — sinal fraco, reconecte"}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              className="bg-white text-red-600 font-bold text-xs px-3 py-1 rounded hover:bg-red-50 transition-colors"
              onClick={() => { fetchWAStatus(); setShowQR(true); }}
              data-testid="wa-reconnect-btn"
            >
              RECONECTAR
            </button>
            {(waStatus === "reconnect_required" || waStatus === "stale" || waStatus === "qr_required") && (
              <button
                className="bg-red-800 text-white font-bold text-xs px-3 py-1 rounded hover:bg-red-900 transition-colors"
                onClick={handleResetSession}
                data-testid="wa-reset-session-btn"
                title="Limpa a sessão antiga e gera um novo QR Code"
              >
                RESETAR SESSÃO
              </button>
            )}
          </div>
        </div>
      )}

      {/* Stale connection warning */}
      {staleConnection && connected && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2 text-amber-800 text-sm flex-shrink-0">
          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
          <span>
            <strong>Conexao possivelmente inativa</strong> — conectado ha {staleAgeDays} dias sem reconexao.
            O WhatsApp pode ter encerrado a sessao. Clique em{" "}
            <button className="underline font-semibold" onClick={() => { handleDisconnect(); setTimeout(() => setShowQR(true), 800); }}>
              Reconectar
            </button>{" "}
            para verificar.
          </span>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden relative">
        {showSettings && (
          <ChatBotSettingsPanel
            botSettings={botSettings}
            saveSettings={saveSettings}
            authAxios={authAxios}
            onClose={() => setShowSettings(false)}
          />
        )}
        {showQR && <QRConnectionPanel authAxios={authAxios} onClose={() => setShowQR(false)} onConnected={() => { setConnected(true); fetchSettings(); }} />}
        {showCustomerDetail && <CustomerDetail phone={showCustomerDetail} authAxios={authAxios} onClose={() => setShowCustomerDetail(null)} />}

        {/* Left: Contact List — full-width on mobile, w-64 sidebar on desktop */}
        <div className={`${mobileView === "chat" ? "hidden md:flex" : "flex"} w-full md:w-64 bg-white border-r flex-col flex-shrink-0`} data-testid="contacts-sidebar">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Buscar" className="pl-9 h-9 bg-gray-50 border-0 text-sm" data-testid="contact-search" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filteredContacts.length === 0 ? (
              <div className="text-center py-8 px-4">
                {!connected ? (
                  <><WifiOff className="w-8 h-8 text-gray-300 mx-auto mb-3" /><p className="text-sm text-gray-400">WhatsApp no conectado</p>
                  <Button size="sm" className="mt-3 bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={() => setShowQR(true)} data-testid="connect-whatsapp-btn"><QrCode className="w-3.5 h-3.5 mr-1.5" /> Conectar</Button></>
                ) : (
                  <><MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-3" /><p className="text-sm text-gray-400">Sin conversaciones</p></>
                )}
              </div>
            ) : filteredContacts.map(c => (
              <ContactItem key={c.id} contact={c} selected={selectedContact} onClick={() => { setSelectedContact(c); setMobileView("chat"); }} />
            ))}
          </div>
          <div className={`px-4 py-2 border-t text-xs flex items-center gap-2 ${staleConnection ? "text-amber-600 bg-amber-50" : connected ? "text-emerald-600 bg-emerald-50" : "text-gray-400 bg-gray-50"}`}>
            {staleConnection ? (
              <><AlertTriangle className="w-3 h-3" /> Conectado (sessao antiga)</>
            ) : connected ? (
              <><Wifi className="w-3 h-3" /> Conectado</>
            ) : (
              <><WifiOff className="w-3 h-3" /> Desconectado</>
            )}
          </div>
        </div>

        {/* Right: Chat Area — full-width on mobile when a contact is selected */}
        <div className={`${mobileView === "contacts" ? "hidden md:flex" : "flex"} flex-1 flex-col bg-[#e5ddd5]`}>
          {selectedContact ? (
            <>
              <div className="bg-white border-b px-3 py-2.5 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                  <button className="md:hidden shrink-0 p-1 -ml-1 text-gray-500 hover:text-gray-700" onClick={() => setMobileView("contacts")} aria-label="Voltar">
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div className="flex items-center gap-3 cursor-pointer min-w-0" onClick={() => setShowCustomerDetail(selectedContact.phone)} data-testid="open-customer-detail">
                    <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center shrink-0"><Bot className="w-4 h-4 text-gray-500" /></div>
                    <div className="min-w-0">
                      <p className="font-bold text-sm truncate">{selectedContact.name || selectedContact.phone}</p>
                      <p className="text-xs text-gray-400">+{selectedContact.phone} · Ver ficha</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {activeSession?.id && (
                    <Badge className="text-[10px] bg-indigo-100 text-indigo-700 gap-1" data-testid="ai-active-badge">
                      <Bot className="w-3 h-3" /> IA Ativa
                    </Badge>
                  )}
                  <Badge className={`text-[10px] ${chatBotEnabled ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                    {chatBotEnabled ? `IA ${aiName}` : "Manual"}
                  </Badge>
                  {chatBotEnabled && (
                    <button
                      onClick={() => toggleContactBot(selectedContact)}
                      title={selectedContact?.bot_paused ? "Retomar IA nesta conversa" : "Pausar IA nesta conversa"}
                      className={`flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full border transition-colors ${
                        selectedContact?.bot_paused
                          ? "bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100"
                          : "bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100"
                      }`}
                    >
                      <Bot className="w-3 h-3" />
                      {selectedContact?.bot_paused ? "IA pausada" : "Pausar IA"}
                    </button>
                  )}
                </div>
              </div>
              {selectedContact?.bot_paused && (
                <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between gap-3 flex-shrink-0">
                  <div className="flex items-center gap-2 text-amber-800 text-xs font-medium">
                    <Bot className="w-3.5 h-3.5 shrink-0" />
                    <span>
                      IA pausada neste contato
                      {selectedContact?.bot_paused_explicit
                        ? " — cliente pediu atendimento humano"
                        : " — pausado pelo operador"}
                      {selectedContact?.bot_paused_at && (
                        <span className="text-amber-600 ml-1">
                          · {new Date(selectedContact.bot_paused_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      )}
                    </span>
                  </div>
                  <button
                    onClick={() => toggleContactBot(selectedContact)}
                    className="text-xs font-bold text-amber-700 bg-amber-100 hover:bg-amber-200 border border-amber-300 px-3 py-1 rounded-full transition-colors shrink-0"
                  >
                    Retomar IA
                  </button>
                </div>
              )}
              <div className="flex flex-1 overflow-hidden">
                <div className="flex-1 flex flex-col">
                  <div className="flex-1 overflow-y-auto p-4" data-testid="messages-area">
                    {messages.length === 0 && <div className="text-center py-12"><MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-3" /><p className="text-sm text-gray-400">Sin mensajes</p></div>}
                    {messages.map((m, i) => <MessageBubble key={m.id || i} message={m} />)}
                    <div ref={messagesEndRef} />
                  </div>
                  <form onSubmit={handleSend} className={`border-t px-4 py-3 flex items-center gap-3 flex-shrink-0 ${!connected ? "bg-red-50" : "bg-white"}`}>
                    {!connected && (
                      <div className="flex-1 flex items-center gap-2 text-red-500 text-xs font-medium">
                        <WifiOff className="w-3.5 h-3.5 flex-shrink-0" />
                        WhatsApp desconectado — reconecte para enviar
                      </div>
                    )}
                    {connected && (
                      <>
                        <Input value={newMessage} onChange={e => setNewMessage(e.target.value)} placeholder="Escribir mensaje" className="flex-1 bg-gray-50 border-0 h-10" data-testid="message-input" />
                        <Button type="submit" size="icon" className="bg-emerald-600 hover:bg-emerald-700 h-10 w-10" disabled={sending || !newMessage.trim()} data-testid="send-message-btn">
                          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                      </>
                    )}
                  </form>
                </div>
                {activeSession?.id && <AISessionPanel session={activeSession} />}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Bot className="w-16 h-16 text-emerald-500/30 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-600 mb-2">IA {aiName}</h2>
                <p className="text-sm text-gray-400 max-w-sm">{connected ? "Selecciona una conversación." : "Conecta tu WhatsApp para empezar."}</p>
                {!connected && <Button className="mt-4 bg-emerald-600 hover:bg-emerald-700 gap-2" onClick={() => setShowQR(true)} data-testid="connect-whatsapp-main-btn"><QrCode className="w-4 h-4" /> Conectar</Button>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
